import { api } from './api';

// GroupHoliday wires a holiday into a group with a chosen variant.
// The opening-hours endpoint is also here because both are scoped per group.
export const groupHolidaysApi = api.injectEndpoints({
  endpoints: (build) => ({
    getGroupHolidays: build.query({
      query: (groupId) => ({ url: `group/${groupId}/holidays`, method: 'GET' }),
      transformResponse: (r) => r.data,
      providesTags: (_r, _e, groupId) => [
        { type: 'GroupHolidays', id: `group-${groupId}` },
      ],
    }),
    createGroupHoliday: build.mutation({
      query: ({ groupId, ...body }) => ({
        url: `group/${groupId}/holiday`,
        method: 'POST',
        body: JSON.stringify(body),
      }),
      invalidatesTags: (_r, _e, { groupId }) => [
        { type: 'GroupHolidays', id: `group-${groupId}` },
      ],
    }),
    updateGroupHoliday: build.mutation({
      query: ({ id, groupId, ...body }) => ({
        url: `group-holiday/${id}`,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
      invalidatesTags: (_r, _e, { groupId }) => [
        { type: 'GroupHolidays', id: `group-${groupId}` },
      ],
    }),
    deleteGroupHoliday: build.mutation({
      query: ({ id, groupId }) => ({ url: `group-holiday/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { groupId }) => [
        { type: 'GroupHolidays', id: `group-${groupId}` },
      ],
    }),

    // Per-group, per-year combined view: enabled holidays with computed
    // dates and their opening-hours rows attached.
    getGroupOpeningHours: build.query({
      query: ({ groupId, year }) => ({
        url: `group/${groupId}/opening-hours?year=${year}`,
        method: 'GET',
      }),
      transformResponse: (r) => r.data,
      providesTags: (_r, _e, { groupId, year }) => [
        { type: 'GroupOpeningHours', id: `group-${groupId}-${year}` },
      ],
    }),
    createOpeningHours: build.mutation({
      // year + groupId double as cache-key inputs and as required body
      // fields the backend validates ("group_id, holiday_id and year are
      // required"). Stripping them from the body broke the create call
      // because year was no longer sent — fold them back in here.
      query: ({ year, groupId, ...body }) => ({
        url: 'group-holiday-opening-hours',
        method: 'POST',
        body: JSON.stringify({ ...body, year, group_id: body.group_id || groupId }),
      }),
      invalidatesTags: (_r, _e, { groupId, year }) => [
        { type: 'GroupOpeningHours', id: `group-${groupId}-${year}` },
      ],
    }),
    updateOpeningHours: build.mutation({
      query: ({ id, year, groupId, ...body }) => ({
        url: `group-holiday-opening-hours/${id}`,
        method: 'PUT',
        body: JSON.stringify({ ...body, year, group_id: body.group_id || groupId }),
      }),
      invalidatesTags: (_r, _e, { groupId, year }) => [
        { type: 'GroupOpeningHours', id: `group-${groupId}-${year}` },
      ],
    }),
    deleteOpeningHours: build.mutation({
      query: ({ id }) => ({ url: `group-holiday-opening-hours/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, { groupId, year }) => [
        { type: 'GroupOpeningHours', id: `group-${groupId}-${year}` },
      ],
    }),
  }),
});

export const {
  useGetGroupHolidaysQuery,
  useCreateGroupHolidayMutation,
  useUpdateGroupHolidayMutation,
  useDeleteGroupHolidayMutation,
  useGetGroupOpeningHoursQuery,
  useCreateOpeningHoursMutation,
  useUpdateOpeningHoursMutation,
  useDeleteOpeningHoursMutation,
} = groupHolidaysApi;
